import { animate, state, style, transition, trigger } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { UIBasicIconsEnum, UseUIBasicIconDirective } from '../atoms';
import { ButtonDirective } from '../molecules';

export enum ModalActionType {
  save,
  delete,
  cancel,
}

export interface ModalAction {
  label: string;
  action: string;
  type: ModalActionType;
  disabled: boolean;
}

export interface Modals {
  title: string;
  description?: string;
  icon?: UIBasicIconsEnum;
  showCloseButton?: boolean;
  actions: ModalAction[];
  backdropDismiss?: boolean;
  isFullscreen?: boolean;
}

const hiddenToVisible = 'hidden => visible';
const visibleToHidden = 'visible => hidden';

@Component({
  selector: 'dmcom-modal',
  imports: [ButtonDirective, UseUIBasicIconDirective],
  animations: [
    trigger('backdrop', [
      state('hidden', style({ opacity: 0 })),
      state('visible', style({ opacity: 1 })),
      transition(hiddenToVisible, animate('300ms ease-out')),
      transition(visibleToHidden, animate('200ms ease-in')),
    ]),
    trigger('modalDesktop', [
      state('hidden', style({ opacity: 0, translate: '-50% -40%', scale: 0.9 })),
      state('visible', style({ opacity: 1, translate: '-50% -50%', scale: 1 })),
      transition(hiddenToVisible, animate('300ms cubic-bezier(0.25,0.8,0.25,1)')),
      transition(visibleToHidden, animate('200ms cubic-bezier(0.4,0,0.6,1)')),
    ]),
    trigger('modalMobile', [
      state('hidden', style({ opacity: 0, scale: 1, translate: '0 0%' })),
      state('visible', style({ opacity: 1, scale: 1, translate: '0 -100%' })),
      transition(hiddenToVisible, animate('300ms cubic-bezier(0.25,0.8,0.25,1)')),
      transition(visibleToHidden, animate('200ms cubic-bezier(0.4,0,0.6,1)')),
    ]),
  ],
  template: `
    <div
      [class.modal-backdrop]="isBackdropVisible()"
      [@backdrop]="isModalVisible() ? 'visible' : 'hidden'"
      (click)="modalOptions().backdropDismiss && onTriggerAction('cancel', $event)"
    ></div>

    <div
      class="
        flex flex-col modal-container
        bg-surface-background-alt shadow-dark
        w-svw tablet-portrait:w-400
        [&.fullscreen]:w-svw [&.fullscreen]:h-svh
        [&:not(.fullscreen)]:max-h-[calc(100svh-80px)]
        fixed top-full left-0 z-10000
        tablet-portrait:top-1/2 tablet-portrait:left-1/2
        tablet-portrait:-translate-x-1/2
        tablet-portrait:-translate-y-1/2
        overflow-hidden
        transition-transform duration-300
      "
      [class.pointer-events-none]="!isModalVisible()"
      [class.rounded-lg]="!modalOptions().isFullscreen && !isMobile()"
      [class.rounded-t-lg]="!modalOptions().isFullscreen && isMobile()"
      [class.fullscreen]="modalOptions().isFullscreen"
      [@modalDesktop]="!isMobile() ? (isModalVisible() ? 'visible' : 'hidden') : 'hidden'"
      [@modalMobile]="isMobile() ? (isModalVisible() ? 'visible' : 'hidden') : 'hidden'"
      (click)="modalOptions().backdropDismiss && $event.stopPropagation()"
    >
      <div class="sticky top-0 z-10 flex flex-col gap-16 items-center justify-center text-headers px-24 pt-32 pb-0">
        <div class="flex flex-col gap-8 py-16 items-center justify-center">
          @if (modalOptions().icon) {
            <div class="flex items-center justify-center">
              <svg class="size-32 text-primary-500" [dmcomUseUIBasicIcon]="modalOptions().icon ?? 'check-circle'"></svg>
            </div>
          }
          <h3 class="typo-subheader font-semibold text-headers leading-24">
            {{ modalOptions().title }}
          </h3>
        </div>

        @if (modalOptions().description) {
          <div class="text-paragraphs typo-p3 leading-20 text-center" [innerHTML]="modalOptions().description"></div>
        }
      </div>
      <div class="overflow-y-auto flex-1">
        <div class="content-container px-24 py-16">
          <ng-content />
        </div>
        <div class="sticky bottom-0 z-10 flex gap-8 px-24 py-8 border-t border-header-stroke bg-surface-background-alt">
          @for (action of modalOptions().actions; track action.label) {
            <button
              dmcomButton
              variant="filled"
              size="lg"
              [color]="action.type === ModalActionType.cancel ? 'neutral' : 'primary'"
              class="flex-1"
              [disabled]="action.disabled"
              (click)="onTriggerAction(action.action)"
            >
              {{ action.label }}
            </button>
          }
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.25);
        z-index: 999;

        & + .modal-container {
          z-index: 1000;
        }
      }
    `,
  ],
})
export class ModalComponent {
  public readonly modalOptions = input.required<Modals>();
  public readonly isModalVisible = input.required<boolean>();
  public readonly triggerAction = output<string>();

  public readonly isBackdropVisible = signal(false);
  public readonly isMobile = toSignal(
    inject(BreakpointObserver)
      .observe('(max-width: 600px)')
      .pipe(map((result) => result.matches)),
    { initialValue: false },
  );

  public ModalActionType = ModalActionType;

  constructor() {
    effect(() => this.isBackdropVisible.set(this.isModalVisible()));
  }

  public getButtonClasses(action: ModalAction): string {
    const baseClasses = 'transition-colors duration-200 flex-1';

    switch (action.type) {
      case ModalActionType.save:
        return `${baseClasses} hover:bg-button-primary-filled-hovered`;
      case ModalActionType.delete:
        return `${baseClasses} hover:bg-button-primary-filled-hovered`;
      case ModalActionType.cancel:
        return `${baseClasses} hover:bg-transparency-white-5`;
      default:
        return `${baseClasses} hover:bg-transparency-white-5`;
    }
  }

  public onTriggerAction(action: string, $event?: MouseEvent): void {
    if ($event) {
      $event.stopPropagation();
      $event.preventDefault();
    }
    this.triggerAction.emit(action);
  }
}
